import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../auth';
import { siteRequestHeaders } from '../../lib/siteRequestHeaders';
import { playMentionSound, unlockMentionSound } from './mentionSound';

const EMPTY = { unread_count: 0, items: [], chat_sound_enabled: true };
const Context = createContext({ ...EMPTY, markSeen: () => {}, error: '' });
const URL = `${process.env.REACT_APP_BACKEND_URL}/api/chat/mentions`;

export const ChatMentionsProvider = ({ children }) => {
  const { user } = useAuth();
  const owner = user?.id || null;
  const [state, setState] = useState(EMPTY), [error, setError] = useState('');
  const currentOwner = useRef(owner), controller = useRef(null), sequence = useRef(0);
  const seen = useRef(new Set()), initialTime = useRef(null), pendingReads = useRef(new Set());
  currentOwner.current = owner;
  const refresh = useCallback(async () => {
    if (!owner || controller.current?.signal.aborted) return;
    const request = ++sequence.current;
    try {
      const { data } = await axios.get(URL, { headers: siteRequestHeaders(), signal: controller.current?.signal, timeout: 15000 });
      if (currentOwner.current !== owner || request !== sequence.current) return;
      const fresh = initialTime.current && data.items.some((item) => !seen.current.has(item.message_id) && item.created_at > initialTime.current);
      if (!initialTime.current) initialTime.current = data.server_time;
      data.items.forEach((item) => seen.current.add(item.message_id));
      if (seen.current.size > 2000) seen.current = new Set([...seen.current].slice(-1000));
      setState(data); setError('');
      if (fresh && data.chat_sound_enabled) playMentionSound();
    } catch (failure) {
      if (!axios.isCancel(failure) && currentOwner.current === owner) setError('Mention notifications are temporarily unavailable.');
    }
  }, [owner]);
  useEffect(() => {
    controller.current = new AbortController(); sequence.current += 1;
    seen.current = new Set(); initialTime.current = null; pendingReads.current = new Set();
    setState(EMPTY); setError('');
    if (owner) refresh();
    const timer = owner ? setInterval(refresh, 3000) : null;
    window.addEventListener('gv-privacy-updated', refresh);
    window.addEventListener('focus', refresh);
    const cleared = (event) => {
      sequence.current += 1;
      setState((current) => { const items = current.items.filter((item) => item.created_at > event.detail); return { ...current, items, unread_count: items.length, cleared_before: event.detail }; });
      refresh();
    };
    window.addEventListener('gv-chat-cleared', cleared);
    const deleted = (event) => {
      sequence.current += 1;
      setState((current) => { const items = current.items.filter((item) => item.message_id !== event.detail); return { ...current, items, unread_count: Math.max(0, current.unread_count - (current.items.length - items.length)) }; });
      refresh();
    };
    window.addEventListener('gv-chat-message-deleted', deleted);
    return () => { controller.current.abort(); clearInterval(timer); window.removeEventListener('gv-privacy-updated', refresh); window.removeEventListener('focus', refresh); window.removeEventListener('gv-chat-cleared', cleared); window.removeEventListener('gv-chat-message-deleted', deleted); };
  }, [owner, refresh]);
  useEffect(() => {
    window.addEventListener('pointerdown', unlockMentionSound, true);
    window.addEventListener('keydown', unlockMentionSound, true);
    return () => { window.removeEventListener('pointerdown', unlockMentionSound, true); window.removeEventListener('keydown', unlockMentionSound, true); };
  }, []);
  const markSeen = useCallback(async (ids) => {
    const selected = ids.filter((id) => !pendingReads.current.has(id)).slice(0, 100);
    if (!owner || !selected.length) return;
    selected.forEach((id) => pendingReads.current.add(id));
    try {
      await axios.post(`${URL}/read`, { message_ids: selected }, { headers: siteRequestHeaders(), signal: controller.current?.signal });
      if (currentOwner.current === owner) await refresh();
    } catch { /* Keep unread state and retry when messages remain visible. */ }
    finally { selected.forEach((id) => pendingReads.current.delete(id)); }
  }, [owner, refresh]);
  return <Context.Provider value={{ ...(owner ? state : EMPTY), markSeen, error }}>{children}</Context.Provider>;
};

export const useChatMentions = () => useContext(Context);